import React, { useState } from 'react';
import { Eye, EyeOff, Facebook, Twitter, Instagram, Mail } from 'lucide-react';
import axios from 'axios';

axios.defaults.withCredentials = true;

const API_URL = process.env.REACT_APP_API_URL || 'https://brown-pelican-168360.hostingersite.com/api';

// ... existing imports
// NOTE: I am replacing the component body to include the new logic cleanly.

export default function Login({ onLogin, onSwitch }) {
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [matchingStudios, setMatchingStudios] = useState([]);
  const [needsSelection, setNeedsSelection] = useState(false);

  const handleSubmit = async (e, targetStudioId = null) => {
    if (e) e.preventDefault();

    if (isResetting) {
      if (!form.email) return alert("Please enter your email");
      try {
        const { data } = await axios.post(`${API_URL}/auth/forgot-password`, { email: form.email });
        if (data.success) {
          alert(data.message);
          setIsResetting(false);
        } else {
          alert(data.message);
        }
      } catch (error) {
        alert("Error: " + (error.response?.data?.message || "Failed to send reset link"));
      }
      return;
    }

    try {
      const { data } = await axios.post(`${API_URL}/studios/login`, {
        email: form.email,
        password: form.password,
        target_studio_id: targetStudioId
      });

      if (data.success) {
        if (data.needsSelection) {
          setMatchingStudios(data.studios);
          setNeedsSelection(true);
        } else {
          const userData = data.user || data.studio;
          // Ensure username is mapped correctly for both types
          if (!userData.username && userData.owner_name) {
            userData.username = userData.owner_name;
          }
          // Store JWT token for API authentication
          if (data.token) {
            localStorage.setItem('bookink_token', data.token);
            axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
          }
          localStorage.setItem('bookink_user', JSON.stringify(userData));
          onLogin(userData);
        }
      } else {
        alert(data.message || "Login failed");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message;
      alert("Login error: " + errorMsg);
    }
  };

  if (needsSelection) {
    return (
      <div className="flex min-h-screen bg-[#f8f9fa] items-center justify-center p-6 animate-fade-in">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 border border-slate-100 space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-bold text-slate-800">Select Studio</h1>
            <p className="text-slate-500 text-sm">Your account is associated with multiple studios. Please select one to continue.</p>
          </div>
          <div className="space-y-3">
            {matchingStudios.map(studio => (
              <button
                key={studio.studio_id}
                onClick={() => handleSubmit(null, studio.studio_id)}
                className="w-full flex items-center gap-4 p-4 border border-slate-200 rounded-xl hover:border-[#5C5CFF] hover:bg-slate-50 transition group text-left"
              >
                <div className="w-12 h-12 rounded-lg bg-slate-100 flex-shrink-0 overflow-hidden">
                  {studio.logo ? (
                    <img src={studio.logo} alt={studio.studio_name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center font-bold text-slate-400">
                      {studio.studio_name.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-bold text-slate-700 group-hover:text-[#5C5CFF] transition">{studio.studio_name}</div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider font-bold">Artist Account</div>
                </div>
              </button>
            ))}
          </div>
          <button
            onClick={() => { setNeedsSelection(false); setMatchingStudios([]); }}
            className="w-full py-3 text-slate-500 font-bold hover:text-slate-700 transition"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-white font-sans animate-fade-in">
      {/* LEFT SIDE - ILLUSTRATION (Unchanged) */}
      <div className="hidden lg:flex w-1/2 bg-[#f8f9fa] relative items-center justify-center p-12">
        <div className="absolute top-8 left-8">
          <img
            src="/logo-marketing.jpg"
            alt="BookInk Logo"
            className="h-12 w-auto object-contain"
          />
        </div>

        <div className="relative w-full max-w-lg">
          {/* Floating Stats Card 1 */}
          <div className="absolute -top-12 -left-8 bg-white p-4 rounded-xl shadow-lg border border-slate-100 flex items-center gap-3 animate-float-slow z-10">
            <div className="w-10 h-10 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center font-bold">🛒</div>
            <div>
              <div className="text-lg font-black text-slate-800">155k</div>
              <div className="text-xs text-slate-500">Total Orders</div>
            </div>
          </div>

          {/* Main Illustration */}
          <img
            src="/auth-login-illustration-light.png"
            alt="Login Illustration"
            className="w-full object-contain relative z-0 drop-shadow-2xl"
          />

          {/* Floating Stats Card 2 */}
          <div className="absolute bottom-12 -right-4 bg-white p-4 rounded-xl shadow-lg border border-slate-100 flex items-center gap-3 animate-float-delayed z-10">
            <div className="w-10 h-10 bg-green-100 text-green-600 rounded-lg flex items-center justify-center font-bold">📈</div>
            <div>
              <div className="text-lg font-black text-slate-800">$42.5k</div>
              <div className="text-xs text-green-500 font-bold">+62% Sessions</div>
            </div>
          </div>

          {/* Floating Stats Card 3 */}
          <div className="absolute top-0 right-0 bg-white p-4 rounded-xl shadow-lg border border-slate-100 flex items-center gap-3 animate-float z-10">
            <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center font-bold">👤</div>
            <div>
              <div className="text-lg font-black text-slate-800">8,458</div>
              <div className="text-xs text-slate-500">New Customers</div>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE - FORM */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="space-y-4">
            <h1 className="text-3xl font-bold text-slate-800">
              {isResetting ? "Forgot Password? 🔒" : "Welcome to BOOKINK! 👋"}
            </h1>
            <p className="text-slate-500">
              {isResetting ? "Enter your email and we'll send you a temporary password." : "Please sign-in to your account and start the adventure"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Email or Username</label>
              <input
                type="email"
                name="email"
                autoComplete="username"
                className="w-full border border-slate-300 rounded-lg px-4 py-3 focus:outline-none focus:border-[#5C5CFF] focus:ring-1 focus:ring-[#5C5CFF] transition"
                placeholder="Enter your email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>

            {!isResetting && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    autoComplete="current-password"
                    className="w-full border border-slate-300 rounded-lg px-4 py-3 focus:outline-none focus:border-[#5C5CFF] focus:ring-1 focus:ring-[#5C5CFF] transition pr-10"
                    placeholder="Enter your password"
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3.5 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
            )}

            {!isResetting && (
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-[#5C5CFF] focus:ring-[#5C5CFF]" />
                  <span className="text-sm text-slate-600">Remember Me</span>
                </label>
                <button type="button" onClick={() => setIsResetting(true)} className="text-sm text-[#5C5CFF] font-bold hover:underline">Forgot Password?</button>
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-[#5C5CFF] hover:bg-[#4b4be6] text-white font-bold py-3 rounded-lg shadow-lg shadow-indigo-200 transition transform active:scale-95"
            >
              {isResetting ? "SEND RESET LINK" : "SIGN IN"}
            </button>

            {isResetting && (
              <button
                type="button"
                onClick={() => setIsResetting(false)}
                className="w-full text-slate-500 font-bold py-2 hover:text-slate-700"
              >
                Back to Login
              </button>
            )}
          </form>

          {!isResetting && (
            <>
              <div className="text-center text-sm text-slate-500">
                New on our platform? <button onClick={onSwitch} className="text-[#5C5CFF] font-bold hover:underline">Create an account</button>
              </div>

              <div className="relative py-4">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
                <div className="relative flex justify-center"><span className="bg-white px-4 text-xs text-slate-400 uppercase tracking-widest">or sign in with</span></div>
              </div>

              <div className="flex gap-4 justify-center">
                <button type="button" className="w-10 h-10 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center hover:bg-white hover:shadow-md transition text-red-500 font-bold group">
                  <Mail size={20} className="group-hover:scale-110 transition" />
                </button>
                <button type="button" className="w-10 h-10 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center hover:bg-white hover:shadow-md transition text-blue-600 font-bold group">
                  <Facebook size={20} className="group-hover:scale-110 transition" />
                </button>
                <button type="button" className="w-10 h-10 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center hover:bg-white hover:shadow-md transition text-sky-400 font-bold group">
                  <Twitter size={20} className="group-hover:scale-110 transition" />
                </button>
                <button type="button" className="w-10 h-10 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center hover:bg-white hover:shadow-md transition text-pink-600 font-bold group">
                  <Instagram size={20} className="group-hover:scale-110 transition" />
                </button>
              </div>

            </>
          )}
        </div>
      </div>
    </div>
  );
}
